import React, { Component } from "react";
import Spinner from "../../spinner/spinner";
import { Table, Row, Modal } from "antd";
import { graphql, compose } from "react-apollo";
import moment from 'moment-timezone';
import { PhotoImage, PhotoPlaceholder } from "../../common/photoImage/photoImage";
import { deactivateUser } from "../../../graphql/mutations/deactivateUser";
import { getCurrentChurchMembers } from "./queries/getCurrentChurchMembers";
import { GroupsFilter } from './groupsFilter';
import EditableCell from './editableCell';
import { valuesComparator } from "../../../utilities/comparators";
import { churchMemberRegistrationDateCountdown } from "../../../utilities/churchRevival";
import { ROLES } from '../../authorization/roles';
import pluralize from 'pluralize';
import "./churchMembmersTable.css";

const PHASES_TO_GROUP_CHANGING = [0, 1];

const DeactivateLink = ({churchMember, onDeactivate}) => {
  return <a onClick={() => { onDeactivate(churchMember) }}>Deactivate</a>;
};

class ChurchMembersTable extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: false }
  }

  get dataSource() {
    return this.props.data.church.churchMembers.edges.map(edges => edges.node).filter(this.groupFilter).map((member, index) => {
      return(
        {
          key: ++index,
          id: member.id,
          photo: member.photo.small,
          name: member.name,
          group: member.group.name,
          groupId: member.group.id,
          joined: member.joined,
          registrationRevivalPhase: member.registrationRevivalPhase,
          registrationRevivalDay: member.registrationRevivalDay,
          email: member.email
        }
      );
    });
  }

  get columns() {
    const columns = [{
      title: '#',
      dataIndex: 'key',
    }, {
      title: 'Profile pic',
      dataIndex: 'photo',
      className: 'church-members-table__photo-column',
      render: (value) => {
        return {children: value ? <PhotoImage imageURL={value}/> : <PhotoPlaceholder/>};
      }
    }, {
      title: 'Name',
      dataIndex: 'name',
      sorter: (firstMember, secondMember) => {
        return valuesComparator(firstMember.name, secondMember.name);
      }
    }, {
      title: 'Group',
      dataIndex: 'group',
      render: this.isAvailableToChangeGroup ? this.renderEditableCell : null
    }, {
      title: 'Joined',
      dataIndex: 'joined',
      sorter: (firstMember, secondMember) => {
        return valuesComparator(firstMember.joined, secondMember.joined);
      },
      render: (value, row) => {
        return {children: <span>{moment.unix(row.joined).format("DD.MM.YYYY")} {this.revivalJoinedInfo(row)}</span>};
      }
    }, {
      title: 'Email',
      dataIndex: 'email',
      sorter: (firstMember, secondMember) => {
        return valuesComparator(firstMember.email, secondMember.email);
      }
    }];

    return this.isEvangelist ? columns.concat(this.actionsCell) : columns;
  }

  get churchRevival() {
    return this.props.selectedChurch.revival;
  }

  get paginationSettings() {
    return(
      {
        pageSize: 20,
        itemRender: (current, type, originalElement) => {
          if (type === "prev") {
            return <a>{"< Back"}</a>;
          } else if (type === "next") {
            return <a>{"Next >"}</a>;
          }
          return originalElement;
        }
      }
    );
  }

  get groupIdFromUrl() {
    const query = new URLSearchParams(this.props.history.location.search);
    return query.get('groupId') || undefined;
  }

  get isEvangelist() {
    return this.props.user.type === ROLES.evangelist;
  }

  get isAvailableToChangeGroup() {
    return this.isEvangelist && PHASES_TO_GROUP_CHANGING.includes(this.churchRevival.phase);
  }

  get actionsCell() {
    return [{
      title: 'Actions',
      dataIndex: '',
      key: 'x',
      render: (_value, churchMember) => <DeactivateLink churchMember={churchMember} onDeactivate={this.onDeactivate} />
    }];
  }

  revivalJoinedInfo(churchMember) {
    switch(churchMember.registrationRevivalPhase) {
      case 0:
        return <span>(Before Event)</span>;
      case 1:
        const daysCount = churchMemberRegistrationDateCountdown(this.churchRevival, moment.unix(churchMember.joined), churchMember.registrationRevivalPhase);
        return <span>({pluralize('Day', daysCount, true)} to Event)</span>;
      case 2:
        return <span>(Day {churchMember.registrationRevivalDay} of Event)</span>;
      case 3:
        return <span>(After Event)</span>;
    }
  }

  mutationParams(churchMemberId) {
    return {
      variables: {
        input: {
          id: churchMemberId
        }
      },
      refetchQueries: [{
        query: getCurrentChurchMembers,
        variables: { churchId: this.props.selectedChurch.id }
      }]
    };
  }

  async submitData(params) {
    try {
      this.setState({ loading: true });
      this.props.mutate(params)
    }
    finally {
      this.setState({ loading: false });
    }
  }

  onDeactivate = (churchMember) => {
    Modal.confirm({
      title: `Are you sure you want to deactivate ${churchMember.name}?`,
      width: 510,
      okText: 'Deactivate',
      onOk: () => { this.submitData(this.mutationParams(churchMember.id)) }
    });
  };

  groupFilter = (member) => {
    return this.groupIdFromUrl ? member.group.id.toString() === this.groupIdFromUrl : true;
  };

  renderEditableCell = (value, record) => {
    return <EditableCell value={value} churchMember={record} groups={this.props.data.church.groups}/>
  };

  render() {
    if (this.props.data.loading) {
      return <Spinner />;
    }
    return (
      <div>
        <Row>
          <GroupsFilter {...this.props} currentValue={this.groupIdFromUrl}/>
        </Row>
        <Row>
          <Table className="church-members-table"
            columns={this.columns}
            dataSource={this.dataSource}
            pagination={this.paginationSettings}
            bordered
          />
        </Row>
      </div>
    );
  }
}

export default compose(
  graphql(getCurrentChurchMembers, {
    options: ({ selectedChurch }) => ({ variables: { churchId: selectedChurch.id } })
  }),
  graphql(deactivateUser)
)(ChurchMembersTable);




// WEBPACK FOOTER //
// ./src/components/pages/churchMembers/churchMembersTable.js